import { useState, useEffect, useRef } from 'react';
import { scoringApi } from '@/services/api';
import { DotRating } from '../ui/DotRating';

interface ScoringTabProps {
  initiativeId: string;
  canEdit: boolean;
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide whitespace-nowrap">
        {label}
      </span>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
  );
}

export function ScoringTab({ initiativeId, canEdit }: ScoringTabProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [impact, setImpact] = useState(0);
  const [effort, setEffort] = useState(0);
  const [showSaved, setShowSaved] = useState(false);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setIsLoading(true);
    scoringApi.get(initiativeId)
      .then((data) => {
        if (data && Object.keys(data).length > 0) {
          setImpact(data.impact ?? 0);
          setEffort(data.effort ?? 0);
        }
      })
      .catch((err) => console.error('Failed to load scoring:', err))
      .finally(() => setIsLoading(false));
  }, [initiativeId]);

  const save = (next: { impact: number; effort: number }) => {
    scoringApi.update(initiativeId, {
      impact: next.impact || null,
      effort: next.effort || null,
    })
      .then(() => {
        if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
        setShowSaved(true);
        savedTimeoutRef.current = setTimeout(() => setShowSaved(false), 2000);
      })
      .catch((err) => console.error('Failed to save scoring:', err));
  };

  const handleImpactChange = (v: number) => {
    setImpact(v);
    save({ impact: v, effort });
  };

  const handleEffortChange = (v: number) => {
    setEffort(v);
    save({ impact, effort: v });
  };

  if (isLoading) {
    return (
      <div className="space-y-5 animate-pulse">
        <div className="h-4 w-24 bg-gray-100 rounded" />
        <div className="h-9 bg-gray-100 rounded-md" />
        <div className="h-9 bg-gray-100 rounded-md" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Auto-save feedback — reserves space so layout doesn't shift */}
      <div className="h-4">
        {canEdit
          ? showSaved && <p className="text-xs text-green-600">✓ Saved</p>
          : <p className="text-xs text-text-tertiary">You can view but not edit this initiative's scoring.</p>
        }
      </div>

      {/* ── Impact ───────────────────────────────────────────────── */}
      <div className="space-y-3">
        <SectionDivider label="Impact" />
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-secondary">How much value will this deliver?</p>
          <div className="flex items-center gap-3">
            <DotRating
              value={impact}
              color="green"
              editable={canEdit}
              onChange={handleImpactChange}
            />
            <span className="text-sm font-medium text-text-primary w-8 text-right tabular-nums">
              {impact > 0 ? `${impact}/5` : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Effort ───────────────────────────────────────────────── */}
      <div className="space-y-3">
        <SectionDivider label="Effort" />
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-secondary">How much work will this take?</p>
          <div className="flex items-center gap-3">
            <DotRating
              value={effort}
              color="purple"
              editable={canEdit}
              onChange={handleEffortChange}
            />
            <span className="text-sm font-medium text-text-primary w-8 text-right tabular-nums">
              {effort > 0 ? `${effort}/5` : '—'}
            </span>
          </div>
        </div>
      </div>

      <p className="text-xs text-text-tertiary pt-2">
        Anyone can vote for this initiative using the 👍 button in the header. Impact, Effort and votes
        drive the prioritisation view and the Impact vs Effort chart.
      </p>
    </div>
  );
}
